import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Package, Truck, Shield, Send, Download, Star, ChevronRight } from 'lucide-react';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';
import basemati_rice from '../assets/agriculture_product/basmati_rice.png';
import chicken_egg from '../assets/agriculture_product/chicken_egg.png';
import corn_seeds from '../assets/agriculture_product/corn_seeds.png';
import guar_gum from '../assets/agriculture_product/guar_gum.png';
import onion from '../assets/agriculture_product/onion.png';
import pulses from '../assets/agriculture_product/pulses.png';
import sesame_seeds from '../assets/agriculture_product/sesame_seeds.png';
import wheat from '../assets/agriculture_product/wheat.png';
import groundnut from '../assets/agriculture_product/groundnut.png';
import cotton from '../assets/agriculture_product/Cotton.jpg';

// Import images (assuming same imports as Products.js for consistency)
import cumin_seeds from '../assets/spice_product/cumin_seeds.png';
import coriander_seeds from '../assets/spice_product/coriander_seeds.png';
import fennel_seeds1 from '../assets/spice_product/fennel_seeds1.png';
import chili_powder from '../assets/spice_product/chili_powder.png';
import coriander_powder from '../assets/spice_product/coriander_powder.png';
import turmeri_powder from '../assets/spice_product/turmeri_powder.png';
import cumin_powder from '../assets/spice_product/cumin_powder.png';

const ProductDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('description');
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        setIsVisible(true);
        window.scrollTo(0, 0);
    }, [id]);

    // Mock data - IDs must match Products.js
    const products = [
        {
            id: 1,
            name: "Premium Basmati Rice",
            category: "Agriculture",
            image: basemati_rice,
            description: "Our Premium Basmati Rice is renowned for its extra-long slender grains that elongate at least twice their original size with a soft and fluffy texture upon cooking.",
            features: [
                "Extra Long Grain",
                "Rich Aroma",
                "Non-Sticky Texture",
                "Aged for 2 Years",
                "High Nutritional Value"
            ],
            specs: {
                "Origin": "India",
                "Moisture": "12% Max",
                "Purity": "95%",
                "Admixture": "1% Max",
                "Broken Ratio": "2% Max",
                "Packaging": "5kg, 10kg, 25kg, 50kg PP Bags"
            },
            minOrder: "1 Metric Ton",
            rating: 5
        },
        {
            id: 2,
            name: "Wheat",
            category: "Agriculture",
            image: wheat,
            description: "Premium quality wheat grains sourced from premium agricultural farms, ideal for flour and grain production.",
            features: [
                "High Protein Content",
                "Clean & Sorted",
                "Excellent Baking Quality",
                "Free from impurities",
                "Golden Color"
            ],
            specs: {
                "Origin": "India",
                "Moisture": "12% Max",
                "Protein": "11% Min",
                "Gluten": "25% Min",
                "Damaged Grains": "1% Max",
                "Packaging": "50kg, 100kg PP/Jute Bags"
            },
            minOrder: "24 Metric Tons",
            rating: 5
        },
        {
            id: 3,
            name: "Onion",
            category: "Agriculture",
            image: onion,
            description: "Fresh, premium quality onions with excellent shelf life, perfect for domestic and international markets.",
            features: [
                "Pungent Flavor",
                "Long Shelf Life",
                "Uniform Size",
                "Firm Texture",
                "Rich Color"
            ],
            specs: {
                "Origin": "India",
                "Color": "Red / Pink / White",
                "Size": "40mm - 70mm+",
                "Packing": "Mesh Bags / Jute Bags",
                "Storage": "Well Ventilated"
            },
            minOrder: "1 Container",
            rating: 5
        },
        {
            id: 4,
            name: "Sesame Seeds",
            category: "Agriculture",
            image: sesame_seeds,
            description: "High-quality sesame seeds rich in minerals and oils, ideal for culinary and industrial applications.",
            features: [
                "High Oil Content",
                "Natural Taste",
                "Sortex Cleaned",
                "Rich in Nutrients",
                "Uniform Color"
            ],
            specs: {
                "Origin": "India",
                "Purity": "99.95% Min",
                "Oil Content": "48% Min",
                "Moisture": "5% Max",
                "FFA": "1.5% Max",
                "Packaging": "Paper/PP Bags"
            },
            minOrder: "18 Metric Tons",
            rating: 5
        },
        {
            id: 5,
            name: "Pulses",
            category: "Agriculture",
            image: pulses,
            description: "Assorted premium pulses including lentils and legumes, rich in protein and nutrients.",
            features: [
                "High Protein",
                "Polished / Unpolished",
                "Quick Cooking",
                "Easy to Digest",
                "Cleaned & Graded"
            ],
            specs: {
                "Varieties": "Toor, Moong, Chana, Masoor, Urad",
                "Moisture": "12% Max",
                "Admixture": "0.5% Max",
                "Packaging": "Consumer Packs / Bulk Bags",
                "Origin": "India"
            },
            minOrder: "20 Metric Tons",
            rating: 5
        },
        {
            id: 6,
            name: "Groundnut",
            category: "Agriculture",
            image: groundnut,
            description: "High-quality groundnuts/peanuts sourced from certified farms, perfect for snacking and oil production.",
            features: [
                "Bold & Java Types",
                "High Oil Content",
                "Aflatoxin Controlled",
                "Crunchy Texture",
                "Nutrient Rich"
            ],
            specs: {
                "Origin": "India",
                "Count": "40/50, 50/60, 60/70",
                "Moisture": "7% Max",
                "Admixture": "1% Max",
                "Oil Content": "48-50%",
                "Packaging": "Jute/PP Bags"
            },
            minOrder: "19 Metric Tons",
            rating: 5
        },
        {
            id: 7,
            name: "Corn Seeds",
            category: "Agriculture",
            image: corn_seeds,
            description: "Premium hybrid corn seeds with high germination rates, ideal for agricultural cultivation.",
            features: [
                "High Yield Potential",
                "Disease Resistant",
                "Uniform Growth",
                "Adaptable to Climates",
                "Certified Quality"
            ],
            specs: {
                "Germination": "98% Min",
                "Purity": "99% Min",
                "Moisture": "12% Max",
                "Variety": "Yellow / White",
                "Packaging": "Customized Seed Packets"
            },
            minOrder: "On Request",
            rating: 5
        },
        {
            id: 8,
            name: "Guar Gum",
            category: "Agriculture",
            image: guar_gum,
            description: "Pure guar gum powder extracted from guar beans, widely used in food and industrial applications.",
            features: [
                "High Viscosity",
                "Fast Hydration",
                "Odourless",
                "Food Grade",
                "Industrial Grade Available"
            ],
            specs: {
                "Viscosity": "3500 - 7000 cps",
                "Mesh Size": "100 / 200 Mesh",
                "Moisture": "12% Max",
                "Protein": "5% Max",
                "Packaging": "25kg Paper Bags"
            },
            minOrder: "20 Metric Tons",
            rating: 5
        },
        {
            id: 9,
            name: "Chicken Egg",
            category: "Agriculture",
            image: chicken_egg,
            description: "Fresh, hygienic chicken eggs sourced from certified farms, rich in protein and nutrients.",
            features: [
                "Farm Fresh",
                "Hygienically Packed",
                "Antibiotic Free",
                "Rich Yolk Color",
                "Strong Shell"
            ],
            specs: {
                "Weight": "50g - 60g",
                "Color": "White / Brown",
                "Shell Condition": "Clean & Intact",
                "Packaging": "Tray Packed in Cartons"
            },
            sizes: ["Small Size", "Medium Size", "Large Size"],
            minOrder: "1 Container",
            rating: 5
        },
        {
            id: 10,
            name: "Cotton Yarn",
            category: "Textiles",
            image: cotton,
            description: "Premium quality organic cotton yarn, perfect for high-end textile manufacturing. Sourced from certified organic farms.",
            features: [
                "100% Organic Cotton",
                "High Tensile Strength",
                "Eco-friendly Dyeing",
                "Soft Texture",
                "Global Organic Textile Standard (GOTS) Certified"
            ],
            specs: {
                "Material": "100% Organic Cotton",
                "Count Range": "NE 10s to 100s",
                "Twist": "Weaving & Knitting Twist",
                "Color": "Raw White / Dyed",
                "Packaging": "Cartons / Pallets"
            },
            minOrder: "500 kg",
            rating: 5
        },
        // {
        //     id: 11,
        //     name: "Spices Collection",
        //     category: "Spices",
        //     image: "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?auto=format&fit=crop&q=80&w=800",
        //     description: "An exquisite collection of hand-picked spices, bringing authentic flavors and aromas to kitchens worldwide.",
        //     features: [
        //         "Hand-picked Selection",
        //         "Authentic Aroma",
        //         "No Artificial Colors",
        //         "Vacuum Packed",
        //         "Lab Tested for Quality"
        //     ],
        //     specs: {
        //         "Origin": "India",
        //         "Grade": "A+ Premium",
        //         "Shelf Life": "12-24 Months",
        //         "Form": "Whole / Powder",
        //         "Packaging": "Customized Packaging Available"
        //     },
        //     minOrder: "100 kg",
        //     rating: 5
        // },
        {
            id: 12,
            name: "Cumin Seeds",
            category: "Spices",
            image: cumin_seeds,
            description: "Pure cumin seeds with authentic aroma and flavor, perfect for cooking and culinary purposes.",
            features: [
                "Strong Aroma",
                "High Volatile Oil",
                "Machine Cleaned",
                "Admixture Free",
                "Intense Flavor"
            ],
            specs: {
                "Origin": "India",
                "Purity": "99% / 99.5%",
                "Moisture": "9% Max",
                "Total Ash": "9.5% Max",
                "Packaging": "25/50kg PP/Jute Bags"
            },
            minOrder: "13 Metric Tons",
            rating: 5
        },
        {
            id: 13,
            name: "Coriander Seeds",
            category: "Spices",
            image: coriander_seeds,
            description: "Premium coriander seeds with strong aromatic properties, ideal for spice blends and cooking.",
            features: [
                "Golden Brown Color",
                "Fresh Aroma",
                "Whole Seeds",
                "Cleaned & Dried",
                "Essential Oil Rich"
            ],
            specs: {
                "Origin": "India",
                "Moisture": "10% Max",
                "Purity": "98% / 99%",
                "Split Seeds": "5% Max",
                "Packaging": "25/40kg PP Bags"
            },
            minOrder: "10 Metric Tons",
            rating: 5
        },
        {
            id: 14,
            name: "Fennel Seeds",
            category: "Spices",
            image: fennel_seeds1,
            description: "High-quality fennel seeds with natural sweetness, widely used in cooking and beverages.",
            features: [
                "Sweet Aroma",
                "Green Color",
                "Digestive Properties",
                "Bold Seeds",
                "Sortex Cleaned"
            ],
            specs: {
                "Origin": "India",
                "Purity": "99%",
                "Moisture": "10% Max",
                "Admixture": "1% Max",
                "Packaging": "25/50kg Bags"
            },
            minOrder: "13 Metric Tons",
            rating: 5
        },
        {
            id: 15,
            name: "Chili Powder",
            category: "Spices",
            image: chili_powder,
            description: "Pure chili powder made from selected red chilies, adding perfect heat and color to dishes.",
            features: [
                "Vibrant Red Color",
                "Consistent Heat",
                "No Artificial Dyes",
                "Finely Ground",
                "Pungent Aroma"
            ],
            specs: {
                "SHU": "20,000 - 90,000",
                "Moisture": "10% Max",
                "Total Ash": "8% Max",
                "Aflatoxin": "B1+B2+G1+G2 < 10ppb",
                "Packaging": "Bulk / Retail Packs"
            },
            minOrder: "10 Metric Tons",
            rating: 5
        },
        {
            id: 16,
            name: "Coriander Powder",
            category: "Spices",
            image: coriander_powder,
            description: "Fine coriander powder ground from premium seeds, essential for authentic Indian cooking.",
            features: [
                "Mild Flavor",
                "Light Brown Color",
                "Free Flowing",
                "Aromatic",
                "No Additives"
            ],
            specs: {
                "Origin": "India",
                "Moisture": "10% Max",
                "Ash": "7% Max",
                "Fineness": "95% passing through mesh",
                "Packaging": "25kg Paper/PP Bags"
            },
            minOrder: "10 Metric Tons",
            rating: 5
        },
        {
            id: 17,
            name: "Turmeric Powder",
            category: "Spices",
            image: turmeri_powder,
            description: "Pure turmeric powder rich in curcumin, used for cooking and health benefits.",
            features: [
                "High Curcumin Content",
                "Bright Yellow Color",
                "Antiseptic Properties",
                "Authentic Taste",
                "No Fillers"
            ],
            specs: {
                "Curcumin": "2% - 5%",
                "Moisture": "10% Max",
                "Ash": "7% Max",
                "Mesh Size": "300 Mesh",
                "Packaging": "20/25kg Kraft Paper Bags"
            },
            minOrder: "15 Metric Tons",
            rating: 5
        },
        {
            id: 18,
            name: "Cumin Powder",
            category: "Spices",
            image: cumin_powder,
            description: "Ground cumin powder with authentic flavor, perfect for seasoning and spice mixes.",
            features: [
                "Earthy Flavor",
                "Dark Brown Color",
                "Freshly Ground",
                "Aromatic",
                "Pure"
            ],
            specs: {
                "Origin": "India",
                "Moisture": "9% Max",
                "Ash": "8% Max",
                "Volatile Oil": "2.5% Min",
                "Packaging": "25kg Bags"
            },
            minOrder: "13 Metric Tons",
            rating: 5
        },
    ];

    const product = products.find(p => p.id === parseInt(id)) || products[0];

    // Related products (same category, excluding current)
    const relatedProducts = products
        .filter(p => p.category === product.category && p.id !== product.id)
        .slice(0, 3);

    return (
        <>
            <Navbar />
            <div className={`min-h-screen bg-gray-50 transition-opacity duration-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>

                {/* Breadcrumb / Back Navigation */}
                <div className="bg-white shadow-sm border-b border-gray-100 sticky top-16 z-30">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <button
                            onClick={() => navigate('/products')}
                            className="flex items-center text-gray-600 hover:text-green-600 transition-colors font-medium"
                        >
                            <ArrowLeft className="w-5 h-5 mr-2" />
                            Back to Products
                        </button>
                    </div>
                </div>

                {/* Main Content */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="grid lg:grid-cols-2 gap-12 mb-16">
                        {/* Image Section */}
                        <div className="space-y-6">
                            <div className="bg-white rounded-3xl p-6 shadow-lg border border-gray-100 overflow-hidden group">
                                <div className="aspect-w-4 aspect-h-3 relative overflow-hidden rounded-2xl bg-white p-4">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-contain mix-blend-multiply transform group-hover:scale-110 transition-transform duration-700"
                                    />
                                    <div className="absolute top-4 right-4">
                                        <span className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold shadow-sm backdrop-blur-sm bg-opacity-90">
                                            {product.category}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-green-50 p-6 rounded-2xl border border-green-100 flex items-center gap-4">
                                    <div className="bg-white p-3 rounded-full shadow-sm text-green-600">
                                        <Truck className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 font-medium">Min. Order</p>
                                        <p className="font-bold text-gray-900">{product.minOrder}</p>
                                    </div>
                                </div>
                                <div className="bg-green-50 p-6 rounded-2xl border border-green-100 flex items-center gap-4">
                                    <div className="bg-white p-3 rounded-full shadow-sm text-green-600">
                                        <Shield className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 font-medium">Quality</p>
                                        <p className="font-bold text-gray-900">Premium Grade</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Product Info Section */}
                        <div className="space-y-8">
                            <div>
                                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
                                    {product.name}
                                </h1>
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="flex text-yellow-400">
                                        {[...Array(product.rating)].map((_, i) => (
                                            <Star key={i} className="w-5 h-5 fill-current" />
                                        ))}
                                    </div>
                                    <span className="text-gray-400">|</span>
                                    <span className="text-gray-600">Export Quality Certified</span>
                                </div>
                                <p className="text-xl text-gray-600 leading-relaxed border-l-4 border-green-500 pl-6">
                                    {product.description}
                                </p>
                            </div>

                            {/* Features Checkmarks */}
                            <div>
                                <h3 className="text-lg font-bold text-gray-900 mb-4">Key Features</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {product.features.map((feature, index) => (
                                        <div key={index} className="flex items-center gap-2 text-gray-700 bg-white p-3 rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                                            <span>{feature}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-100">
                                <Link to="/contact">
                                    <button className="flex-1 bg-gradient-to-r from-green-600 to-emerald-700 text-white px-8 py-4 rounded-xl font-bold text-lg hover:shadow-lg hover:shadow-green-500/30 transform hover:translate-y-[-2px] transition-all duration-300 flex items-center justify-center gap-2">
                                        <Send className="w-5 h-5" />
                                        Contact for Pricing
                                    </button>
                                </Link>
                                {/* <button className="flex-1 bg-white text-gray-800 border-2 border-gray-200 px-8 py-4 rounded-xl font-bold text-lg hover:border-green-500 hover:text-green-600 hover:bg-green-50 transition-all duration-300 flex items-center justify-center gap-2">
                                    <Download className="w-5 h-5" />
                                    Download Brochure
                                </button> */}
                            </div>
                        </div>
                    </div>

                    {/* Specifications Tabs */}
                    <div className="mb-20">
                        <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
                            <div className="flex border-b border-gray-100 overflow-x-auto">
                                <button
                                    className={`px-8 py-5 font-semibold text-lg transition-colors whitespace-nowrap ${activeTab === 'description' ? 'text-green-600 border-b-2 border-green-600 bg-green-50' : 'text-gray-500 hover:text-green-600 hover:bg-gray-50'}`}
                                    onClick={() => setActiveTab('description')}
                                >
                                    Product Specifications
                                </button>
                                <button
                                    className={`px-8 py-5 font-semibold text-lg transition-colors whitespace-nowrap ${activeTab === 'shipping' ? 'text-green-600 border-b-2 border-green-600 bg-green-50' : 'text-gray-500 hover:text-green-600 hover:bg-gray-50'}`}
                                    onClick={() => setActiveTab('shipping')}
                                >
                                    Shipping & Packaging
                                </button>
                            </div>
                            <div className="p-8 lg:p-12">
                                {activeTab === 'description' && (
                                    <div className="animate-fade-in">
                                        <div className={`grid ${product.sizes ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-8`}>
                                            {/* Specs - same as original */}
                                            {Object.entries(product.specs).map(([key, value]) => (
                                                <div key={key} className="flex flex-col border-b border-gray-100 pb-4 last:border-0">
                                                    <span className="text-sm text-gray-400 font-medium uppercase tracking-wider mb-1">{key}</span>
                                                    <span className="text-lg text-gray-900 font-semibold">{value}</span>
                                                </div>
                                            ))}

                                            {/* Right: Sizes column (only for egg) */}
                                            {product.sizes && (
                                                <div className="flex items-center justify-between border-b border-gray-100 pb-4 flex-wrap gap-4">

                                                    <span className="text-sm text-gray-400 font-medium uppercase tracking-wider">
                                                        Available Sizes
                                                    </span>

                                                    <div className="flex gap-2 flex-wrap">
                                                        {product.sizes.map((size, i) => (
                                                            <div
                                                                key={i}
                                                                className="flex items-center gap-2.5 bg-green-50 border border-green-200 rounded-lg px-4 py-2.5"
                                                            >
                                                                <div className="w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center font-bold text-xs flex-shrink-0">
                                                                    {i === 0 ? 'S' : i === 1 ? 'M' : 'L'}
                                                                </div>

                                                                <span className="text-gray-800 font-semibold text-base">
                                                                    {size}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>

                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {activeTab === 'shipping' && (
                                    <div className="space-y-6 animate-fade-in">
                                        <div className="bg-teal-50 p-6 rounded-2xl border border-teal-100">
                                            <h4 className="font-bold text-teal-900 mb-2 flex items-center gap-2">
                                                <Package className="w-5 h-5" /> Packaging Options
                                            </h4>
                                            <p className="text-teal-800">
                                                We offer customizable packaging solutions including Jute Bags, PP Bags, and Non-Woven Bags in various sizes (5kg, 10kg, 25kg, 50kg) with private labeling options.
                                            </p>
                                        </div>
                                        <div className="bg-green-50 p-6 rounded-2xl border border-green-100">
                                            <h4 className="font-bold text-green-900 mb-2 flex items-center gap-2">
                                                <Truck className="w-5 h-5" /> Shipping Information
                                            </h4>
                                            <p className="text-green-800">
                                                Efficient global logistics network ensuring timely delivery. FOB, CIF, and CFR shipment terms available. Full Container Load (FCL) services for maximum efficiency.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Related Products */}
                    {relatedProducts.length > 0 && (
                        <div className="border-t border-gray-200 pt-16">
                            <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">Related <span className="text-green-600">Products</span></h2>
                            <div className="grid md:grid-cols-3 gap-8">
                                {relatedProducts.map((related) => (
                                    <div
                                        key={related.id}
                                        onClick={() => navigate(`/product/${related.id}`)}
                                        className="bg-white rounded-2xl p-4 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 cursor-pointer group"
                                    >
                                        <div className="relative h-48 rounded-xl overflow-hidden mb-4">
                                            <img
                                                src={related.image}
                                                alt={related.name}
                                                className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                                            />
                                        </div>
                                        <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">{related.name}</h3>
                                        <p className="text-sm text-gray-500 mb-4 line-clamp-2">{related.description}</p>
                                        <div className="flex items-center text-green-600 font-semibold text-sm group-hover:underline">
                                            View Details <ChevronRight className="w-4 h-4 ml-1" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <style jsx>{`
                    @keyframes fade-in {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    .animate-fade-in { animation: fade-in 0.5s ease-out forwards; }
                `}</style>
            </div>
            <Footer />
        </>
    );
};

export default ProductDetails;